// Plan 15-07 Task 1 — FriendsScreen.
// Phase 17 Plan 06 Task 1 (cleanup-reskin) — re-skin to prototype tokens +
// Btn + Seg primitives. Three stacked sections preserved (add-friend →
// pending-inbox → friends-list) per existing test pins; Seg picker added
// above as a quick-filter view selector without hiding any DOM section.

import { useStore } from '@nanostores/react'
import { type JSX, useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { useDocumentTitle } from '../../i18n/useDocumentTitle.js'
import {
  type FriendRequestInboxResponse,
  type FriendRequestOutboxResponse,
  fetchFriendsInbox,
  fetchFriendsOutbox,
} from '../../net/friends.js'
import { useRouter } from '../../router/router.js'
import {
  $friendsList,
  initFriendsListListeners,
  refreshFriendsList,
} from '../../stores/friendsList.js'
import { $lobbyData } from '../../stores/lobbyData.js'
import { Btn } from '../components/Btn'
import { Chip } from '../components/Chip'
import { Screen, ScreenHeader } from '../components/Screen.js'
import { Seg, type SegOption } from '../components/Seg'
import { AddFriendForm } from './AddFriendForm.js'
import { FriendOutboxRow } from './FriendOutboxRow.js'
import { FriendRequestRow } from './FriendRequestRow.js'
import { FriendRow, type FriendRowEntry } from './FriendRow.js'
import { ShareMyCodeSheet } from './ShareMyCodeSheet.js'

type FilterTab = 'all' | 'incoming' | 'outgoing'

export interface FriendsScreenProps {
  /** The signed-in player's own friend code. Optional — share sheet hides if absent. */
  readonly ownFriendCode?: string
  /** Optional override of the share-base URL (defaults to a deterministic placeholder). */
  readonly shareBaseUrl?: string
}

function defaultShareBaseUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/add`
  }
  return 'https://runegambit.example/add'
}

export function FriendsScreen({
  ownFriendCode,
  shareBaseUrl,
}: FriendsScreenProps = {}): JSX.Element {
  useDocumentTitle('doc.title.friends')
  const resolvedShareBaseUrl = shareBaseUrl ?? defaultShareBaseUrl()
  const intl = useIntl()
  const router = useRouter()
  const friendsList = useStore($friendsList)
  const lobby = useStore($lobbyData)
  const [inbox, setInbox] = useState<FriendRequestInboxResponse | null>(null)
  const [outbox, setOutbox] = useState<FriendRequestOutboxResponse | null>(null)
  const [shareOpen, setShareOpen] = useState(false)
  const [tab, setTab] = useState<FilterTab>('all')

  const refetchInbox = useCallback(async (): Promise<void> => {
    try {
      const data = await fetchFriendsInbox()
      setInbox(data)
    } catch {
      // Swallow — apiFetch already handled 401/5xx.
    }
  }, [])

  const refetchOutbox = useCallback(async (): Promise<void> => {
    try {
      const data = await fetchFriendsOutbox()
      setOutbox(data)
    } catch {
      // Swallow.
    }
  }, [])

  const refreshAll = useCallback(async (): Promise<void> => {
    await Promise.allSettled([
      refreshFriendsList(),
      refetchInbox(),
      refetchOutbox(),
    ])
  }, [refetchInbox, refetchOutbox])

  useEffect(() => {
    void refreshAll()
    const teardownStore = initFriendsListListeners()
    const onAccepted = (): void => {
      void refetchInbox()
    }
    window.addEventListener('runegambit:friend-request-accepted', onAccepted)
    return () => {
      window.removeEventListener(
        'runegambit:friend-request-accepted',
        onAccepted,
      )
      teardownStore()
    }
  }, [refreshAll, refetchInbox])

  const prevCountsRef = useRef<{
    pending: number | null
    friends: number | null
  }>({ pending: null, friends: null })
  useEffect(() => {
    if (!lobby) return
    const nextPending = lobby.pending_friend_request_count
    const nextFriends = lobby.friends_count
    const prev = prevCountsRef.current
    if (prev.pending !== null && nextPending !== prev.pending) {
      void refetchInbox()
    }
    if (prev.friends !== null && nextFriends !== prev.friends) {
      void refreshFriendsList()
      void refetchOutbox()
    }
    prevCountsRef.current = { pending: nextPending, friends: nextFriends }
  }, [lobby, refetchInbox, refetchOutbox])

  const friends: ReadonlyArray<FriendRowEntry> = (friendsList?.friends ??
    []) as ReadonlyArray<FriendRowEntry>
  const sortedFriends = friends
    .slice()
    .sort((a, b) => a.display_name.localeCompare(b.display_name))
  const requests = inbox?.requests ?? []
  const outboundRequests = outbox?.requests ?? []

  const shareUrl = ownFriendCode
    ? `${resolvedShareBaseUrl.replace(/\/$/, '')}/${ownFriendCode}`
    : null

  const segOptions: ReadonlyArray<SegOption<FilterTab>> = [
    {
      value: 'all',
      label: intl.formatMessage({ id: 'social.friends.tab.friends' }),
    },
    {
      value: 'incoming',
      label:
        requests.length > 0
          ? intl.formatMessage(
              { id: 'social.friends.tab.incoming_count' },
              { count: requests.length },
            )
          : intl.formatMessage({ id: 'social.friends.tab.incoming' }),
    },
    {
      value: 'outgoing',
      label: intl.formatMessage({ id: 'social.friends.tab.outgoing' }),
    },
  ]

  return (
    <Screen
      name="friends"
      gutter={16}
      contentStyle={{ gap: 'var(--space-6)' }}
      mainProps={{ 'data-testid': 'friends-screen' }}
      header={
        <ScreenHeader
          title={
            <FormattedMessage
              id="social.friends.title"
              defaultMessage="Friends"
            />
          }
          onBack={() => router.navigate('/lobby')}
          backLabel={intl.formatMessage({
            id: 'nav.back',
            defaultMessage: 'Back',
          })}
          right={
            ownFriendCode && shareUrl ? (
              <Btn
                kind="gold"
                size="sm"
                onClick={() => setShareOpen(true)}
                ariaLabel={intl.formatMessage({
                  id: 'social.friends.share.title',
                  defaultMessage: 'Share your code',
                })}
                testId="friends-share-mine"
              >
                <FormattedMessage
                  id="social.friends.share.copy_code_cta"
                  defaultMessage="Share my code"
                />
              </Btn>
            ) : undefined
          }
        />
      }
    >
      {ownFriendCode && (
        <div
          className="share-code-pill"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-2) var(--space-3)',
            // Folk friend-code card: frost (House Frost) tint, borderless warm
            // tile relief — echoes the prototype's frost-tinted code card.
            backgroundColor: 'var(--color-player-b-tint)',
            borderRadius: 'var(--radius-pill)',
            boxShadow: 'var(--shadow-tile)',
          }}
        >
          <Chip variant="gold">{ownFriendCode}</Chip>
        </div>
      )}

      <Seg<FilterTab>
        options={segOptions}
        value={tab}
        onChange={setTab}
        ariaLabel={intl.formatMessage({
          id: 'social.friends.tabs',
          defaultMessage: 'Friends view',
        })}
        testId="friends-tabs"
      />

      <section
        aria-label={intl.formatMessage({
          id: 'social.friends.add_by_code',
          defaultMessage: 'Add a friend',
        })}
        data-section="add-friend"
      >
        <h2
          style={{
            margin: '0 0 var(--space-3)',
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-16)',
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage
            id="social.friends.add_by_code"
            defaultMessage="Add a friend"
          />
        </h2>
        <AddFriendForm onSent={() => void refreshAll()} />
      </section>

      <section
        aria-label={intl.formatMessage({
          id: 'social.friends.inbox.title',
          defaultMessage: 'Pending requests',
        })}
        data-section="pending-inbox"
      >
        <h2
          style={{
            margin: '0 0 var(--space-3)',
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-16)',
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage
            id="social.friends.inbox.title"
            defaultMessage="Pending requests"
          />
        </h2>
        {requests.length === 0 ? (
          <p
            data-testid="friends-inbox-empty"
            style={{
              margin: 0,
              fontFamily: 'var(--font-family-body)',
              fontWeight: 500,
              fontSize: 'var(--font-size-14)',
              color: 'var(--color-text-primary)',
              opacity: 0.7,
            }}
          >
            <FormattedMessage
              id="social.friends.inbox.empty"
              defaultMessage="No pending requests."
            />
          </p>
        ) : (
          <ul
            data-testid="friends-inbox-list"
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
            }}
          >
            {requests.map((req) => (
              <li key={req.request_id}>
                <FriendRequestRow
                  row={req}
                  onAccept={() => void refetchInbox()}
                  onReject={() => void refetchInbox()}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {outboundRequests.length > 0 && (
        <section
          aria-label={intl.formatMessage({
            id: 'social.friends.outbox.title',
            defaultMessage: 'Sent',
          })}
          data-section="sent-outbox"
        >
          <h2
            style={{
              margin: '0 0 var(--space-3)',
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-16)',
              color: 'var(--color-text-primary)',
            }}
          >
            <FormattedMessage
              id="social.friends.outbox.title"
              defaultMessage="Sent"
            />
          </h2>
          <ul
            data-testid="friends-outbox-list"
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
            }}
          >
            {outboundRequests.map((row) => (
              <li key={row.request_id}>
                <FriendOutboxRow
                  row={row}
                  onCancelled={() => void refetchOutbox()}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      <section
        aria-label={intl.formatMessage({
          id: 'social.friends.list.title',
          defaultMessage: 'Your friends',
        })}
        data-section="friends-list"
      >
        <h2
          style={{
            margin: '0 0 var(--space-3)',
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-16)',
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage
            id="social.friends.list.title"
            defaultMessage="Your friends"
          />
        </h2>
        {sortedFriends.length === 0 ? (
          <div data-testid="friends-list-empty">
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--font-family-display)',
                fontWeight: 700,
                fontSize: 'var(--font-size-16)',
                color: 'var(--color-text-primary)',
              }}
            >
              <FormattedMessage
                id="social.friends.empty.title"
                defaultMessage="No friends yet"
              />
            </p>
            <p
              style={{
                margin: 'var(--space-1) 0 0',
                fontFamily: 'var(--font-family-body)',
                fontWeight: 500,
                fontSize: 'var(--font-size-14)',
                color: 'var(--color-text-primary)',
                opacity: 0.7,
              }}
            >
              <FormattedMessage
                id="social.friends.empty.body"
                defaultMessage="Share your code to invite someone."
              />
            </p>
          </div>
        ) : (
          <ul
            data-testid="friends-list"
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
            }}
          >
            {sortedFriends.map((friend) => (
              <li key={friend.user_uuid}>
                <FriendRow friend={friend} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {ownFriendCode && shareUrl && (
        <ShareMyCodeSheet
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          friendCode={ownFriendCode}
          shareUrl={shareUrl}
        />
      )}
    </Screen>
  )
}
